<template>
    <!-- The header stopwatch's timer (App.vue): a small panel floating over the app, dragged by its top
         bar. It keeps running while closed or on another page, and survives a reload (localStorage). -->
    <div v-if="$store.state.timer.open" ref="panel" class="ftimer" :class="status" :style="{ left: pos.x + 'px', top: pos.y + 'px' }" role="dialog" aria-label="Timer">
        <div class="ftimer-head" title="Drag to move" @pointerdown="startDrag">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2.5 2.5M10 2h4M12 2v3" /></svg>
            <span>Timer</span>
            <button type="button" class="ftimer-x" aria-label="Close timer" :title="status == 'running' ? 'Close (keeps running)' : 'Close'" @pointerdown.stop @click="$store.state.timer.open = false">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
        </div>

        <form v-if="status == 'idle'" class="ftimer-body" @submit.prevent="start()">
            <label class="ftimer-label" for="ftimer-minutes">Minutes</label>
            <input id="ftimer-minutes" ref="minutesInput" type="number" min="1" max="600" step="1" v-model.number="minutes" />
            <button class="btn ftimer-btn">Start Timer</button>
        </form>

        <div v-else class="ftimer-body">
            <div class="ftimer-time" aria-live="off">{{ label }}</div>
            <div v-if="status == 'done'" class="ftimer-done" role="status">Time's up</div>
            <div v-else-if="status == 'paused'" class="ftimer-sub">Paused</div>
            <div class="ftimer-actions">
                <button v-if="status == 'running'" type="button" class="btn-light ftimer-btn" @click="pause()">Pause</button>
                <button v-else-if="status == 'paused'" type="button" class="btn ftimer-btn" @click="resume()">Resume</button>
                <button type="button" :class="status == 'done' ? 'btn ftimer-btn' : 'btn-light ftimer-btn'" @click="reset()">{{ status == 'done' ? 'Done' : 'Reset' }}</button>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

const STORAGE_KEY = 'floatingTimer';
const WIDTH = 220;
const MARGIN = 8;

export default defineComponent({
    name: 'FloatingTimer',
    data() {
        return {
            minutes: 25,
            status: 'idle', // idle | running | paused | done
            endsAt: 0, // while running: when it hits zero (ms since epoch)
            remainingMs: 0, // while paused (and the last value shown)
            tick: null as any,
            pos: { x: 0, y: 0 },
            placed: false, // dragged somewhere (else it sits under the header on the right)
            drag: null as any,
            audio: null as any
        }
    },
    created() {
        this.restore();
        this.publish();
        if(this.status == 'running') {
            this.startTicking();
        }
    },
    mounted() {
        window.addEventListener('resize', this.clampToWindow);
        this.$nextTick(this.clampToWindow);
    },
    beforeUnmount() {
        clearInterval(this.tick);
        window.removeEventListener('resize', this.clampToWindow);
    },
    computed: {
        // "24:59", or "1:05:00" past an hour
        label(): string {
            const total = Math.ceil(Math.max(0, this.remainingMs) / 1000);
            const h = Math.floor(total / 3600);
            const m = Math.floor((total % 3600) / 60);
            const s = total % 60;
            const ss = String(s).padStart(2, '0');
            return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
        }
    },
    watch: {
        // Opening it: back in view, and the minutes ready to type over
        '$store.state.timer.open'(open: boolean) {
            if(open) {
                this.$nextTick(() => {
                    this.clampToWindow();
                    const input = this.$refs.minutesInput as HTMLInputElement;
                    input?.focus();
                    input?.select();
                });
            }
        }
    },
    methods: {
        start() {
            const minutes = Math.round(Number(this.minutes));
            if(!minutes || minutes < 1) {
                this.minutes = 25;
                return;
            }
            this.minutes = Math.min(600, minutes);
            this.remainingMs = this.minutes * 60000;
            this.endsAt = Date.now() + this.remainingMs;
            this.status = 'running';
            this.prepareSound();
            this.startTicking();
            this.save();
        },
        pause() {
            this.remainingMs = Math.max(0, this.endsAt - Date.now());
            this.status = 'paused';
            clearInterval(this.tick);
            this.save();
        },
        resume() {
            this.endsAt = Date.now() + this.remainingMs;
            this.status = 'running';
            this.prepareSound();
            this.startTicking();
            this.save();
        },
        reset() {
            clearInterval(this.tick);
            this.status = 'idle';
            this.remainingMs = 0;
            this.save();
        },
        // Counts from the clock rather than subtracting each tick, so a background tab stays accurate
        startTicking() {
            clearInterval(this.tick);
            const update = () => {
                this.remainingMs = this.endsAt - Date.now();
                if(this.remainingMs <= 0) {
                    this.finish();
                }
                this.publish();
            };
            update();
            this.tick = setInterval(update, 250);
        },
        finish() {
            clearInterval(this.tick);
            this.remainingMs = 0;
            this.status = 'done';
            this.$store.state.timer.open = true;
            this.chime();
            this.save();
        },
        // The header button's countdown (App.vue)
        publish() {
            this.$store.state.timer.status = this.status;
            this.$store.state.timer.label = this.status == 'idle' ? '' : this.label;
        },
        // Browsers only allow sound after a click, so the audio is set up when Start/Resume is pressed
        prepareSound() {
            try {
                const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
                if(AudioCtx && !this.audio) {
                    this.audio = new AudioCtx();
                }
                this.audio?.resume?.();
            } catch(e) { /* no sound, that's fine */ }
        },
        // Three short beeps
        chime() {
            const ctx = this.audio;
            if(!ctx) {
                return;
            }
            try {
                for(let i = 0; i < 3; i++) {
                    const at = ctx.currentTime + i * 0.35;
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.value = 880;
                    gain.gain.setValueAtTime(0.0001, at);
                    gain.gain.exponentialRampToValueAtTime(0.25, at + 0.02);
                    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.25);
                    osc.connect(gain).connect(ctx.destination);
                    osc.start(at);
                    osc.stop(at + 0.3);
                }
            } catch(e) { /* no sound, that's fine */ }
        },
        startDrag(e: PointerEvent) {
            if(e.button != 0) {
                return;
            }
            const panel = this.$refs.panel as HTMLElement;
            panel?.setPointerCapture?.(e.pointerId);
            this.drag = { dx: e.clientX - this.pos.x, dy: e.clientY - this.pos.y, id: e.pointerId };
            window.addEventListener('pointermove', this.onDrag);
            window.addEventListener('pointerup', this.endDrag);
            e.preventDefault();
        },
        onDrag(e: PointerEvent) {
            if(!this.drag || e.pointerId != this.drag.id) {
                return;
            }
            this.pos = { x: e.clientX - this.drag.dx, y: e.clientY - this.drag.dy };
            this.placed = true;
            this.clampToWindow();
        },
        endDrag() {
            this.drag = null;
            window.removeEventListener('pointermove', this.onDrag);
            window.removeEventListener('pointerup', this.endDrag);
            this.save();
        },
        // Keeps the whole panel on screen; until it's been dragged, it sits under the header on the right
        clampToWindow() {
            const panel = this.$refs.panel as HTMLElement;
            const w = panel?.offsetWidth || WIDTH;
            const h = panel?.offsetHeight || 150;
            if(!this.placed) {
                this.pos = { x: window.innerWidth - w - 20, y: 72 };
            }
            this.pos = {
                x: Math.min(Math.max(MARGIN, this.pos.x), Math.max(MARGIN, window.innerWidth - w - MARGIN)),
                y: Math.min(Math.max(MARGIN, this.pos.y), Math.max(MARGIN, window.innerHeight - h - MARGIN))
            };
        },
        save() {
            this.publish();
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify({
                    minutes: this.minutes, status: this.status, endsAt: this.endsAt, remainingMs: this.remainingMs,
                    pos: this.placed ? this.pos : null
                }));
            } catch(e) { /* not remembered, that's fine */ }
        },
        // A timer that ran out while the page was closed shows as done
        restore() {
            try {
                const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
                if(!saved) {
                    return;
                }
                this.minutes = Number(saved.minutes) || 25;
                if(saved.pos) {
                    this.pos = saved.pos;
                    this.placed = true;
                }
                if(saved.status == 'running') {
                    this.endsAt = Number(saved.endsAt) || 0;
                    if(this.endsAt > Date.now()) {
                        this.status = 'running';
                    } else {
                        this.status = 'done';
                        this.remainingMs = 0;
                        this.$store.state.timer.open = true;
                    }
                } else if(saved.status == 'paused' || saved.status == 'done') {
                    this.status = saved.status;
                    this.remainingMs = Number(saved.remainingMs) || 0;
                }
            } catch(e) { /* start fresh */ }
        }
    }
});
</script>
